/**
 * Inpatient management module.
 */
import { fetchApi, fetchGetFileApi } from '../client';

export const inpatientModule = {
  getActiveRecords: () => fetchApi<any[]>('/api/inpatient/active'),

  admit: (data: any) =>
    fetchApi<any>('/api/inpatient/admit', { method: 'POST', body: JSON.stringify(data) }),

  recordRound: (data: any) =>
    fetchApi<any>('/api/inpatient/round', { method: 'POST', body: JSON.stringify(data) }),

  // Wards & Beds
  getWards: () => fetchApi<any[]>('/api/inpatient/wards'),
  createWard: (data: any) =>
    fetchApi<any>('/api/inpatient/wards', { method: 'POST', body: JSON.stringify(data) }),
  getBeds: (wardId: string) => fetchApi<any[]>(`/api/inpatient/wards/${wardId}/beds`),
  updateBedStatus: (bedId: string, status: string) =>
    fetchApi<any>(`/api/inpatient/beds/${bedId}/status?status=${status}`, { method: 'PATCH' }),

  // Lab tests
  searchLabTests: (query: string) =>
    fetchApi<any[]>(`/api/lab-tests/search?q=${encodeURIComponent(query)}`),
  getLabTests: () => fetchApi<any[]>('/api/lab-tests/active'),
  getLabTestCategories: async () => {
    const tests = await fetchApi<any[]>('/api/lab-tests/active');
    if (tests.error) return tests;
    const categories = Array.from(new Set(tests.data?.map((t: any) => t.category))).filter(Boolean);
    return { data: categories };
  },

  // Single admission (dedicated fetch-by-id)
  getAdmission: (admissionId: string) =>
    fetchApi<any>(`/api/inpatient/admission/${admissionId}`),

  // Discharge (the endpoint always existed server-side; web never called it)
  discharge: (admissionId: string, data?: { dischargeType?: string; overrideReason?: string }) =>
    fetchApi<any>(`/api/inpatient/admission/${admissionId}/discharge`, {
      method: 'POST',
      body: JSON.stringify(data ?? {}),
    }),

  // Intra-hospital transfer (bed/ward move on one admission)
  transfer: (admissionId: string, data: { toBedId: string; reason?: string }) =>
    fetchApi<any>(`/api/inpatient/admission/${admissionId}/transfer`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getBedHistory: (admissionId: string) =>
    fetchApi<any[]>(`/api/inpatient/admission/${admissionId}/bed-history`),

  // Housekeeping: mark a bed cleaned so it can be re-assigned
  markBedCleaned: (bedId: string) =>
    fetchApi<any>(`/api/inpatient/beds/${bedId}/cleaned`, { method: 'PATCH' }),

  // Rounds
  getRoundHistory: (admissionId: string) =>
    fetchApi<any[]>(`/api/inpatient/admission/${admissionId}/rounds`),

  // Nursing assessments (Morse/Braden/pain/handover)
  recordNursingAssessment: (data: any) =>
    fetchApi<any>('/api/inpatient/nursing/assessment', { method: 'POST', body: JSON.stringify(data) }),
  getNursingAssessments: (admissionId: string) =>
    fetchApi<any[]>(`/api/inpatient/nursing/admission/${admissionId}/assessments`),

  // IPD charge capture + billing bridge
  getCharges: (admissionId: string) =>
    fetchApi<any[]>(`/api/inpatient/billing/admission/${admissionId}/charges`),
  postCharge: (admissionId: string, data: any) =>
    fetchApi<any>(`/api/inpatient/billing/admission/${admissionId}/charges`, { method: 'POST', body: JSON.stringify(data) }),
  getBillingSummary: (admissionId: string) =>
    fetchApi<any>(`/api/inpatient/billing/admission/${admissionId}/summary`),
  generateBill: (admissionId: string, finalBill = false) =>
    fetchApi<any>(`/api/inpatient/billing/admission/${admissionId}/generate-bill`, {
      method: 'POST',
      body: JSON.stringify({ finalBill }),
    }),

  // Medication workflow: verify, dispense, PRN
  verifyMedicationOrder: (orderId: string) =>
    fetchApi<any>(`/api/inpatient/enterprise/medication-order/${orderId}/verify`, { method: 'POST' }),
  dispenseMedicationOrder: (orderId: string, quantity = 1) =>
    fetchApi<any>(`/api/inpatient/enterprise/medication-order/${orderId}/dispense`, {
      method: 'POST',
      body: JSON.stringify({ quantity }),
    }),
  recordPrnDose: (orderId: string, data?: { remarks?: string; witnessedBy?: string }) =>
    fetchApi<any>(`/api/inpatient/enterprise/medication-order/${orderId}/prn-dose`, {
      method: 'POST',
      body: JSON.stringify(data ?? {}),
    }),

  // Lab metric templates
  getLabMetricTemplates: () => fetchApi<any[]>('/api/lab/metric-templates'),
  createLabMetricTemplate: (data: any) =>
    fetchApi<any>('/api/lab/metric-templates', { method: 'POST', body: JSON.stringify(data) }),
  updateLabMetricTemplate: (id: number | string, data: any) =>
    fetchApi<any>(`/api/lab/metric-templates/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteLabMetricTemplate: (id: number | string) =>
    fetchApi<any>(`/api/lab/metric-templates/${id}`, { method: 'DELETE' }),

  // AI notes
  saveAiNote: (admissionId: string, note: string) =>
    fetchApi<any>(`/api/inpatient/admission/${admissionId}/ai-note`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),

  // ── INPATIENT_ENHANCED suite (gated by the INPATIENT_ENHANCED module) ──
  // Round-ready deterministic snapshot
  getSnapshot: (admissionId: string) =>
    fetchApi<any>(`/api/inpatient/admission/${admissionId}/snapshot`),
  // Acuity census (MEWS, sickest first)
  getActiveWithAcuity: () => fetchApi<any[]>('/api/inpatient/enhanced/active'),
  // Doctor task inbox
  getTasks: () => fetchApi<any[]>('/api/inpatient/enhanced/tasks'),
  // Admission order sets
  getOrderSets: () => fetchApi<any[]>('/api/inpatient/order-sets'),
  applyOrderSet: (admissionId: string, setId: string) =>
    fetchApi<any>(`/api/inpatient/admission/${admissionId}/apply-order-set/${setId}`, { method: 'POST' }),
  // Auto-drafted discharge summary
  getDischargeDraft: (admissionId: string) =>
    fetchApi<any>(`/api/inpatient/admission/${admissionId}/discharge-summary/draft`),
  // Voice ward-round scribe
  scribeRound: (admissionId: string, transcript: string, language: string) =>
    fetchApi<any>(`/api/inpatient/admission/${admissionId}/round/scribe`, {
      method: 'POST',
      body: JSON.stringify({ transcript, language }),
    }),

  // Enterprise enhancements
  recordIntakeOutput: (data: any) =>
    fetchApi<any>('/api/inpatient/enterprise/intake-output', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getFluidBalance: (admissionId: string, date: string) =>
    fetchApi<any>(`/api/inpatient/enterprise/admission/${admissionId}/fluid-balance?date=${date}`),

  administerMedication: (data: any) =>
    fetchApi<any>('/api/inpatient/enterprise/medication/administer', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  createMedicationOrders: (admissionId: string, payload: any[]) =>
    fetchApi<any>(`/api/inpatient/enterprise/admission/${admissionId}/medications`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  getMedicationOrders: (admissionId: string) =>
    fetchApi<any>(`/api/inpatient/enterprise/admission/${admissionId}/medications`),

  getMarHistory: (admissionId: string) =>
    fetchApi<any>(`/api/inpatient/enterprise/admission/${admissionId}/mar`),

  getActiveAlerts: (admissionId: string) =>
    fetchApi<any>(`/api/inpatient/enterprise/admission/${admissionId}/alerts`),

  acknowledgeAlert: (alertId: string) =>
    fetchApi<any>(`/api/inpatient/enterprise/alerts/${alertId}/acknowledge`, { method: 'POST' }),

  signAndLockRound: (roundId: string) =>
    fetchApi<any>(`/api/inpatient/enterprise/ward-round/${roundId}/sign`, { method: 'POST' }),

  getDischargePlan: (admissionId: string) =>
    fetchApi<any>(`/api/inpatient/enterprise/admission/${admissionId}/discharge-plan`),

  updateDischargePlan: (admissionId: string, data: any) =>
    fetchApi<any>(`/api/inpatient/enterprise/admission/${admissionId}/discharge-plan`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getCarePlans: (admissionId: string) =>
    fetchApi<any[]>(`/api/inpatient/enterprise/admission/${admissionId}/care-plans`),

  createCarePlan: (data: any) =>
    fetchApi<any>('/api/inpatient/enterprise/care-plan', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  completeCarePlan: (planId: string) =>
    fetchApi<any>(`/api/inpatient/enterprise/care-plan/${planId}/complete`, { method: 'PATCH' }),

  getClinicalAudit: (admissionId: string) =>
    fetchApi<any[]>(`/api/inpatient/enterprise/admission/${admissionId}/audit`),

  getAiClinicalAnalysis: (admissionId: string) =>
    fetchApi<any>(`/api/inpatient/enterprise/admission/${admissionId}/ai-analysis`),

  approveAiAnalysis: (approval: any) =>
    fetchApi<any>('/api/ai/approve-analysis', { method: 'POST', body: JSON.stringify(approval) }),

  // Lab orders
  getLabHistory: (admissionId: string) =>
    fetchApi<any[]>(`/api/inpatient/labs/admission/${admissionId}/history`),

  getLabTasks: () => fetchApi<any[]>('/api/inpatient/labs/tasks'),
  getLabClinicHistory: () => fetchApi<any[]>('/api/inpatient/labs/history'),

  // Grouped requisitions: all investigations a doctor ordered together, with patient name + DOB.
  getLabRequisitions: (history = false) =>
    fetchApi<any[]>(`/api/inpatient/labs/requisitions?history=${history}`),

  completeLabOrder: (orderId: string, data: any) =>
    fetchApi<any>(`/api/inpatient/labs/order/${orderId}/complete`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /** Single lab order detail (status/results/documents) — read-only, no side effect. */
  getLabOrderDetail: (orderId: string) =>
    fetchApi<any>(`/api/inpatient/labs/order/${orderId}`),

  orderLabTest: (data: any) =>
    fetchApi<any>('/api/inpatient/labs/order', { method: 'POST', body: JSON.stringify(data) }),

  downloadLabReport: (orderId: string) =>
    fetchGetFileApi(`/api/inpatient/labs/order/${orderId}/report`),

  getLabAnalysis: (orderId: string) =>
    fetchApi<any>(`/api/inpatient/labs/order/${orderId}/analysis`),

  analyzeLabFile: (orderId: string, filePath: string) =>
    fetchApi<any>(`/api/inpatient/labs/order/${orderId}/analyze-file`, {
      method: 'POST',
      body: JSON.stringify({ filePath }),
    }),

  // Structured trend history for one formal LabTestParameter, one patient.
  getParameterTrend: (parameterId: string | number, patientId: string) =>
    fetchApi<any[]>(`/api/inpatient/labs/parameters/${parameterId}/trend?patientId=${patientId}`),

  // Clinic-wide queue of recent HIGH/CRITICAL structured results.
  getCriticalResults: (lookbackDays = 7) =>
    fetchApi<any[]>(`/api/inpatient/labs/critical-results?lookbackDays=${lookbackDays}`),

  // Pharmacy medication inventory (inpatient context)
  pharmacyMedications: {
    create: async (data: any) => {
      const res = await fetchApi<any>('/api/pharmacy-medications', { method: 'POST', body: JSON.stringify(data) });
      if (res.error) throw new Error(res.error);
      return res.data;
    },
    list: async () => {
      const res = await fetchApi<any[]>('/api/pharmacy-medications');
      if (res.error) throw new Error(res.error);
      return res.data || [];
    },
    search: async (query: string) => {
      const res = await fetchApi<any[]>(`/api/pharmacy-medications/search?query=${encodeURIComponent(query)}`);
      if (res.error) throw new Error(res.error);
      return res.data || [];
    },
    getLowStock: async () => {
      const res = await fetchApi<any[]>('/api/pharmacy-medications/low-stock');
      if (res.error) throw new Error(res.error);
      return res.data || [];
    },
    getExpired: async () => {
      const res = await fetchApi<any[]>('/api/pharmacy-medications/expired');
      if (res.error) throw new Error(res.error);
      return res.data || [];
    },
    getExpiringSoon: async () => {
      const res = await fetchApi<any[]>('/api/pharmacy-medications/expiring-soon');
      if (res.error) throw new Error(res.error);
      return res.data || [];
    },
    getById: async (id: string) => {
      const res = await fetchApi<any>(`/api/pharmacy-medications/${id}`);
      if (res.error) throw new Error(res.error);
      return res.data;
    },
    update: async (id: string, data: any) => {
      const res = await fetchApi<any>(`/api/pharmacy-medications/${id}`, { method: 'PUT', body: JSON.stringify(data) });
      if (res.error) throw new Error(res.error);
      return res.data;
    },
    updateStock: async (id: string, quantity: number) => {
      const res = await fetchApi<any>(`/api/pharmacy-medications/${id}/stock`, { method: 'PUT', body: JSON.stringify({ newQuantity: quantity }) });
      if (res.error) throw new Error(res.error);
      return res.data;
    },
    updatePrice: async (id: string, price: number) => {
      const res = await fetchApi<any>(`/api/pharmacy-medications/${id}/price`, { method: 'PUT', body: JSON.stringify({ newPrice: price }) });
      if (res.error) throw new Error(res.error);
      return res.data;
    },
    delete: async (id: string) => {
      const res = await fetchApi<any>(`/api/pharmacy-medications/${id}`, { method: 'DELETE' });
      if (res.error) throw new Error(res.error);
      return res;
    },
  },
};
